import { parseRuntimeSeconds } from './playback'
import { compatibilityScore, type CompatibilityEvidence } from './compatibility'
import { scoreHdr, type HdrCapabilities, type HdrFormat } from './hdr'
import type { CodecCapabilities, CodecSupport } from './codecs'
import type { HdrMode, StremioStream } from '../types'
import { streamRemoteKey } from './remoteProtocol'

function streamText(stream: StremioStream) {
  return [
    stream.name,
    stream.title,
    stream.description,
    stream.behaviorHints?.filename,
    stream.url?.split(/[?#]/)[0],
  ].filter(Boolean).join(' ')
}

/** Match a newly resolved signed URL to the source already playing without
 * ever comparing or exposing its token. Query-only rotations use the opaque
 * stable key; CDN path rotations require all available release metadata. */
export function findRefreshedStream(original: StremioStream, candidates: StremioStream[]) {
  const originalKey = streamRemoteKey(original)
  const exact = candidates.find((candidate) => candidate.url !== original.url && streamRemoteKey(candidate) === originalKey)
  if (exact) return exact
  const filename = original.behaviorHints?.filename || ''
  const release = original.title || original.description || original.name || ''
  const size = original.behaviorHints?.videoSize || 0
  if (!filename && !release) return undefined
  return candidates.find((candidate) => (
    Boolean(candidate.url && candidate.url !== original.url)
    && (!original.addonUrl || candidate.addonUrl === original.addonUrl)
    && (filename ? candidate.behaviorHints?.filename === filename : true)
    && (release ? (candidate.title || candidate.description || candidate.name || '') === release : true)
    && (size ? candidate.behaviorHints?.videoSize === size : true)
  ))
}

export function getStreamResolution(stream: StremioStream) {
  const text = streamText(stream)
  if (/\b(2160p?|4k|uhd)\b/i.test(text)) return { key: '2160p', label: '4K', rank: 2160 }
  if (/\b1080[pi]?\b/i.test(text)) return { key: '1080p', label: '1080P', rank: 1080 }
  if (/\b720[pi]?\b/i.test(text)) return { key: '720p', label: '720P', rank: 720 }
  if (/\b480[pi]?\b/i.test(text)) return { key: '480p', label: '480P', rank: 480 }
  if (/\b360[pi]?\b/i.test(text)) return { key: '360p', label: '360P', rank: 360 }
  return { key: 'auto', label: 'AUTO', rank: 0 }
}

function parseCompactNumber(raw: string) {
  const suffix = raw.slice(-1).toLowerCase()
  const multiplier = suffix === 'k' ? 1_000 : suffix === 'm' ? 1_000_000 : 1
  const numeric = multiplier === 1 ? raw : raw.slice(0, -1)
  const normalized = multiplier === 1
    ? numeric.replace(/[.,](?=\d{3}(?:\D|$))/g, '')
    : numeric.replace(',', '.')
  const value = Number.parseFloat(normalized)
  return Number.isFinite(value) ? Math.round(value * multiplier) : 0
}

export function getStreamPeers(stream: StremioStream) {
  const text = streamText(stream)
  const match = text.match(/👤\s*([\d.,]+\s*[km]?)/i)
    || text.match(/(?:seeders?|peers?)\s*[:=-]?\s*([\d.,]+\s*[km]?)/i)
  return match ? parseCompactNumber(match[1].replace(/\s/g, '')) : 0
}

function parseDecimal(raw: string) {
  const normalized = raw.includes(',') && raw.includes('.')
    ? raw.lastIndexOf(',') > raw.lastIndexOf('.')
      ? raw.replace(/\./g, '').replace(',', '.')
      : raw.replace(/,/g, '')
    : raw.replace(',', '.')
  const value = Number.parseFloat(normalized)
  return Number.isFinite(value) ? value : 0
}

export function getStreamSize(stream: StremioStream) {
  const hinted = stream.behaviorHints?.videoSize
  if (typeof hinted === 'number' && Number.isFinite(hinted) && hinted > 0) return hinted

  const text = streamText(stream)
  const match = text.match(/💾\s*([\d.,]+)\s*(tib|tb|gib|gb|mib|mb|kib|kb|bytes?|b)\b/i)
    || text.match(/(?:file\s*)?size\s*[:=-]?\s*([\d.,]+)\s*(tib|tb|gib|gb|mib|mb|kib|kb|bytes?|b)\b/i)
  if (!match) return 0

  const unit = match[2].toLowerCase()
  const power = unit.startsWith('t') ? 4 : unit.startsWith('g') ? 3 : unit.startsWith('m') ? 2 : unit.startsWith('k') ? 1 : 0
  return Math.round(parseDecimal(match[1]) * (1024 ** power))
}

/* ------------------------------------------------------------------
   What the panel can actually decode.
   The release name already carries this; it just was not being read.
   ------------------------------------------------------------------ */

export type Codec = 'h264' | 'hevc' | 'av1' | 'vp9' | 'xvid' | ''
export type Audio = 'aac' | 'ac3' | 'eac3' | 'dts' | 'truehd' | 'flac' | 'opus' | ''
export type Container = 'mp4' | 'mkv' | 'webm' | 'ts' | 'avi' | ''

export function getStreamCodec(stream: StremioStream): Codec {
  const text = streamText(stream)
  if (/\b(av1|av01)\b/i.test(text)) return 'av1'
  if (/\b(x265|h\.?265|hevc)\b/i.test(text)) return 'hevc'
  if (/\bvp9\b/i.test(text)) return 'vp9'
  if (/\b(xvid|divx)\b/i.test(text)) return 'xvid'
  if (/\b(x264|h\.?264|avc)\b/i.test(text)) return 'h264'
  return ''
}

export function getStreamAudio(stream: StremioStream): Audio {
  const text = streamText(stream)
  if (/\b(truehd|atmos)\b/i.test(text)) return 'truehd'
  if (/\b(dts[-\s.]?(hd|x|es)?|dtshd)\b/i.test(text)) return 'dts'
  // DDP5.1 and EAC3 have no word boundary after the token, so do not require one
  if (/\b(ddp|e-?ac-?3|dd\+)/i.test(text)) return 'eac3'
  if (/\b(ac-?3|dd\s?5\.1|dolby\s?digital)/i.test(text)) return 'ac3'
  if (/\bflac\b/i.test(text)) return 'flac'
  if (/\bopus\b/i.test(text)) return 'opus'
  if (/\b(aac|m4a)\b/i.test(text)) return 'aac'
  return ''
}

export function getStreamContainer(stream: StremioStream): Container {
  const text = streamText(stream)
  if (/\.(?:m2ts|ts)(?:\b|$)|\bmpeg-?ts\b/i.test(text)) return 'ts'
  if (/\.mkv(?:\b|$)|\bmatroska\b/i.test(text)) return 'mkv'
  if (/\.webm(?:\b|$)|\bwebm\b/i.test(text)) return 'webm'
  if (/\.(?:avi|divx)(?:\b|$)|\bavi\b/i.test(text)) return 'avi'
  if (/\.(?:mp4|m4v)(?:\b|$)|\bmp4\b/i.test(text)) return 'mp4'
  return ''
}

export function getStreamHdr(stream: StremioStream): HdrFormat {
  const text = streamText(stream)
  if (/\b(dv|dovi|dolby[-\s.]?vision)\b/i.test(text)) return 'Dolby Vision'
  // `+` is not a word character, so a trailing `\b` fails before a space.
  // Match the delimiter explicitly or HDR10+ is silently classified as HDR10.
  if (/\bhdr10\+(?=$|[^a-z0-9])/i.test(text)) return 'HDR10+'
  if (/\b(?:hdr10|hdr)\b/i.test(text)) return 'HDR10'
  return ''
}

/** Formats a webOS media pipeline is unlikely to decode in-browser. */
const CODEC_PENALTY: Record<Codec, number> = { av1: 70, hevc: 18, vp9: 30, xvid: 25, h264: 0, '': 6 }
const AUDIO_PENALTY: Record<Audio, number> = { truehd: 45, dts: 40, flac: 25, opus: 12, eac3: 0, ac3: 0, aac: 0, '': 5 }
const CONTAINER_PENALTY: Record<Container, number> = { avi: 30, webm: 8, ts: 3, mkv: 0, mp4: 0, '': 3 }

/**
 * Roughly the least bitrate a real encode of each resolution needs, in bytes
 * per second. A stream claiming 1080p in a fraction of this is not the title
 * it says it is — a sample, a fragment, or a mislabelled entry — and must not
 * be recommended just because it is small and cheap to fetch.
 */
const MIN_PLAUSIBLE_BITRATE: Record<string, number> = {
  '2160p': 1_000_000,
  '1080p': 312_500,
  '720p': 187_500,
  '480p': 75_000,
  '360p': 50_000,
}

export function implausiblySmall(resolutionKey: string, bitrate: number) {
  const floor = MIN_PLAUSIBLE_BITRATE[resolutionKey]
  if (!floor || !bitrate) return false
  return bitrate < floor * 0.25
}

export interface StreamChoice {
  stream: StremioStream
  resolution: ReturnType<typeof getStreamResolution>
  peers: number
  size: number
}

export interface RankedStream extends StreamChoice {
  codec: Codec
  audio: Audio
  container: Container
  hdr: string
  /** bytes per second the file needs to play without stalling, 0 when unknown */
  bitrate: number
  score: number
  risks: string[]
  /** Of those risks, the ones about decoding the file rather than fetching it. */
  decodeRisks: string[]
  /** This exact panel reported it cannot decode this format, or has failed on
   * it before. Stronger than the name-based heuristics. */
  provenUndecodable: boolean
  /** Positive measured evidence is not a warning. */
  evidence: string
  /** False when selecting this stream would violate the viewer's HDR policy. */
  allowed: boolean
  policyBlocked: boolean
}

export interface RankContext {
  /** runtime string from Cinemeta, e.g. "49 min" */
  runtime?: string
  /** measured seconds of media, when the player already knows it */
  durationSeconds?: number
  /** bytes per second this connection has actually achieved */
  measuredSpeed?: number
  hdrCapabilities?: HdrCapabilities
  hdrMode?: HdrMode
  codecCapabilities?: CodecCapabilities
  /** Results measured on this exact TV/runtime, stronger than generic probes. */
  compatibility?: CompatibilityEvidence
  /**
   * Owner policy from the admin interface. 'strict' removes anything this
   * panel is unlikely to decode instead of merely ranking it lower; 'any'
   * leaves the list untouched. A size cap in GB hides heavier files.
   */
  codecPolicy?: 'strict' | 'balanced' | 'any'
  maxFileSizeBytes?: number
  /** Offer a lighter file alongside each recommendation. */
  offerAlternative?: boolean
}

export function streamCompatibilityKey(stream: StremioStream) {
  return [
    getStreamResolution(stream).key,
    getStreamCodec(stream) || 'codec-unknown',
    getStreamAudio(stream) || 'audio-unknown',
    getStreamContainer(stream) || 'container-unknown',
    getStreamHdr(stream) || 'SDR',
  ].join('|')
}

export function streamTransportCompatibilityKey(stream: StremioStream, transport: 'hls' | 'direct') {
  return `${streamCompatibilityKey(stream)}|transport:${transport}`
}

/** A configured preference is the default. Repeated measured failure on that
 * exact TV can flip it to the transport which has actually succeeded. */
export function preferredTransport(
  stream: StremioStream,
  preferHls: boolean,
  evidence: CompatibilityEvidence = {},
): 'hls' | 'direct' {
  const preferred = preferHls ? 'hls' : 'direct'
  const alternate = preferHls ? 'direct' : 'hls'
  const preferredResult = evidence[streamTransportCompatibilityKey(stream, preferred)]
  const alternateResult = evidence[streamTransportCompatibilityKey(stream, alternate)]
  const preferredFailing = preferredResult && preferredResult.failures > preferredResult.successes
  const alternateWorking = alternateResult && alternateResult.successes >= alternateResult.failures
  return preferredFailing && alternateWorking ? alternate : preferred
}

function probedPenalty(fallback: number, support: CodecSupport | undefined) {
  if (!support || support === 'unknown') return fallback
  if (support === 'supported') return 0
  if (support === 'maybe') return Math.min(fallback, 8)
  return Math.max(fallback, 65)
}

function codecProbe(codec: Codec, capabilities?: CodecCapabilities) {
  if (!capabilities) return undefined
  if (codec === 'h264' || codec === 'hevc' || codec === 'vp9' || codec === 'av1') return capabilities[codec]
  return codec === 'xvid' ? 'unsupported' : undefined
}

function audioProbe(audio: Audio, capabilities?: CodecCapabilities) {
  if (!capabilities) return undefined
  if (audio === 'aac' || audio === 'ac3' || audio === 'eac3' || audio === 'opus') return capabilities[audio]
  if (audio === 'dts' || audio === 'truehd' || audio === 'flac') return 'unsupported'
  return undefined
}

function durationOf(context: RankContext) {
  return context.durationSeconds || parseRuntimeSeconds(context.runtime) || 0
}

export function describeStream(stream: StremioStream, context: RankContext = {}): RankedStream {
  const resolution = getStreamResolution(stream)
  const size = getStreamSize(stream)
  const peers = getStreamPeers(stream)
  const codec = getStreamCodec(stream)
  const audio = getStreamAudio(stream)
  const container = getStreamContainer(stream)
  const hdr = getStreamHdr(stream)
  const duration = durationOf(context)
  const bitrate = size && duration ? size / duration : 0

  const risks: string[] = []
  const decodeRisks: string[] = []
  let provenUndecodable = false
  let policyBlocked = false
  let score = 100

  // people want the best picture they can actually sustain, so resolution
  // counts for something — just not enough to outweigh "it will not play"
  score += Math.min(24, resolution.rank / 90)

  const decodeRisk = (message: string, proven = false) => {
    risks.push(message)
    decodeRisks.push(message)
    if (proven) provenUndecodable = true
  }

  const codecSupport = codecProbe(codec, context.codecCapabilities)
  const codecPenalty = probedPenalty(CODEC_PENALTY[codec], codecSupport)
  score -= codecPenalty
  if (codecPenalty >= 25) decodeRisk(`${codec.toUpperCase()} may not decode${codecSupport === 'unsupported' ? ' on this TV' : ''}`, codecSupport === 'unsupported')
  const audioSupport = audioProbe(audio, context.codecCapabilities)
  const audioPenalty = probedPenalty(AUDIO_PENALTY[audio], audioSupport)
  score -= audioPenalty
  if (audioPenalty >= 25) decodeRisk(`${audio.toUpperCase()} audio may not decode${audioSupport === 'unsupported' ? ' on this TV' : ''}`, audioSupport === 'unsupported')
  score -= CONTAINER_PENALTY[container]
  if (CONTAINER_PENALTY[container] >= 25) decodeRisk(`${container.toUpperCase()} container may require transcoding`)

  if (context.hdrCapabilities) {
    const hdrScore = scoreHdr(hdr, context.hdrCapabilities, context.hdrMode || 'auto')
    score += hdrScore.score
    policyBlocked = hdrScore.policyBlocked
    if (hdrScore.risk) risks.push(hdrScore.risk)
  }

  // a file the connection cannot sustain will stall, whatever its seed count
  if (bitrate && context.measuredSpeed) {
    const headroom = context.measuredSpeed / bitrate
    if (headroom < 1) {
      score -= 60
      risks.push('Faster than this connection')
    } else if (headroom < 1.3) {
      score -= 25
      risks.push('Little headroom')
    }
  }

  // Seeds decide whether a torrent starts at all. A direct HTTP/HLS stream has
  // no seed count by design and must not be punished as a dead torrent.
  const torrent = Boolean(stream.infoHash || peers)
  if (torrent && peers <= 0) { score -= 40; risks.push('No seeders reported') }
  else if (torrent) score += Math.min(20, Math.log10(peers + 1) * 9)
  else if (stream.url) score += 8

  // among equals, prefer the leaner file — but leanness is only a virtue up to
  // the point where the file is too small to be the title at all
  if (implausiblySmall(resolution.key, bitrate)) {
    score -= 70
    risks.push(`Too small to be ${resolution.label}`)
  } else if (bitrate) {
    score -= Math.min(12, bitrate / (1024 * 1024) * 2)
  }

  const measured = compatibilityScore(streamCompatibilityKey(stream), context.compatibility)
  score += measured.score
  // A failure recorded on this exact runtime is measurement, not guesswork.
  if (measured.risk) decodeRisk(measured.risk, true)

  return {
    stream, resolution, peers, size, codec, audio, container, hdr, bitrate, score, risks,
    decodeRisks, provenUndecodable, evidence: measured.proof,
    allowed: !policyBlocked,
    policyBlocked,
  }
}

/**
 * The owner's policy, applied before anything is ranked or shown.
 *
 * The three levels are genuinely different: `strict` removes anything with a
 * decode concern at all, `balanced` removes only what this panel has actually
 * reported or demonstrated it cannot play, and `any` removes nothing. None of
 * them hide a source for being merely slow — a thin connection is the viewer's
 * to judge, and `strict` used to drop those too, which could empty the list on
 * a busy network.
 */
export function allowedByPolicy(entry: RankedStream, context: RankContext = {}) {
  if (context.maxFileSizeBytes && entry.size && entry.size > context.maxFileSizeBytes) return false
  if (context.codecPolicy === 'strict' && entry.decodeRisks.length) return false
  if (context.codecPolicy === 'balanced' && entry.provenUndecodable) return false
  return true
}

export function rankStreams(streams: StremioStream[], context: RankContext = {}): RankedStream[] {
  return streams
    .map((stream) => describeStream(stream, context))
    .filter((entry) => allowedByPolicy(entry, context))
    .sort((a, b) => Number(b.allowed) - Number(a.allowed) || b.score - a.score || b.resolution.rank - a.resolution.rank || a.size - b.size)
}

/** Best source that does not violate a hard viewer policy. Blocked sources
 * remain visible in rankStreams/groupStreams so the source panel can explain. */
export function bestAllowedStream(streams: StremioStream[], context: RankContext = {}) {
  return rankStreams(streams, context).find((entry) => entry.allowed)
}

export interface StreamGroup {
  resolution: ReturnType<typeof getStreamResolution>
  primary: RankedStream
  alternative?: RankedStream
  others: RankedStream[]
}

/**
 * Grouped by resolution, highest first. Each group leads with the stream this
 * device can best sustain, offers a much smaller well-seeded alternative when
 * one exists, and keeps the rest for "show all".
 */
export function groupStreams(streams: StremioStream[], context: RankContext = {}): StreamGroup[] {
  const groups = new Map<string, RankedStream[]>()
  rankStreams(streams, context).forEach((entry) => {
    const list = groups.get(entry.resolution.key) || []
    list.push(entry)
    groups.set(entry.resolution.key, list)
  })

  return Array.from(groups.values()).map((ranked) => {
    const primary = ranked[0]
    const rest = ranked.slice(1)
    const peerFloor = primary.peers > 0 ? Math.max(5, Math.min(50, Math.ceil(primary.peers * 0.1))) : 5
    const alternative = context.offerAlternative !== false && primary.resolution.key !== 'auto' && primary.size > 0
      ? rest
        .filter((entry) => entry.size > 0 && entry.size <= primary.size * 0.6 && entry.peers >= peerFloor)
        .sort((a, b) => a.size - b.size || b.peers - a.peers)[0]
      : undefined
    return {
      resolution: primary.resolution,
      primary,
      alternative,
      others: rest.filter((entry) => entry !== alternative),
    }
  }).sort((a, b) => b.resolution.rank - a.resolution.rank)
}
